import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { AnalyticsApiService } from '../../shared/api/analytics-api.service';
import {
  AnalyticsCategoryBreakdownDto,
  AnalyticsDashboardDto,
  AnalyticsStatusBreakdownDto
} from '../../shared/api/analytics-api.types';
import { LoaderComponent } from '../../shared/ui/loader/loader.component';

type StatusChartSegment = {
  item: AnalyticsStatusBreakdownDto;
  dasharray: string;
  dashoffset: number;
};

@Component({
  selector: 'app-analytics',
  standalone: true,
  imports: [CommonModule, LoaderComponent],
  templateUrl: './analytics.component.html',
  styleUrl: './analytics.component.css'
})
export class AnalyticsComponent implements OnInit, OnDestroy {
  dashboard: AnalyticsDashboardDto | null = null;
  loading = true;
  errorMessage = '';
  activeStatusKey: string | null = null;

  readonly statusChartRadius = 52;
  readonly statusChartStrokeWidth = 24;
  readonly statusChartCircumference = 2 * Math.PI * this.statusChartRadius;

  private readonly destroy$ = new Subject<void>();

  constructor(private readonly analyticsApi: AnalyticsApiService) {}

  ngOnInit(): void {
    this.loadDashboard();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get maxCategoryCount(): number {
    return Math.max(...(this.dashboard?.categoryBreakdown ?? []).map((item) => item.totalIdeas), 0);
  }

  get categoryAxisTicks(): number[] {
    const max = this.maxCategoryCount;
    if (max <= 0) {
      return [0, 3, 6, 9, 12];
    }

    const step = Math.max(1, Math.ceil(max / 4));
    return Array.from({ length: 5 }, (_, index) => step * index).reverse();
  }

  loadDashboard(): void {
    this.loading = true;
    this.errorMessage = '';

    this.analyticsApi
      .getDashboard()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (dashboard) => {
          this.dashboard = dashboard;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.errorMessage = 'Failed to load analytics. Please try again.';
        }
      });
  }

  getCategoryBarHeight(item: AnalyticsCategoryBreakdownDto): string {
    if (this.maxCategoryCount <= 0) {
      return '0%';
    }

    const height = (item.totalIdeas / this.maxCategoryCount) * 100;
    return `${Math.max(14, Math.round(height))}%`;
  }

  getStatusColor(key: string): string {
    const normalizedKey = (key ?? '').replace(/[\s_-]/g, '').toLowerCase();
    const palette: Record<string, string> = {
      submitted: '#ef4444',
      triage: '#ec4899',
      underreview: '#2563eb',
      needmoreinfo: '#3b82f6',
      accepted: '#f59e0b',
      planned: '#8b5cf6',
      inprogress: '#06b6d4',
      delivered: '#10b981',
      rejected: '#9ca3af',
      archived: '#6b7280'
    };

    return palette[normalizedKey] ?? '#94a3b8';
  }

  getStatusSegments(items: AnalyticsStatusBreakdownDto[]): StatusChartSegment[] {
    let offset = 0;

    return items.map((item) => {
      const segmentSize = this.getStatusSegmentDegrees(item, items);
      const segmentLength = (segmentSize / 360) * this.statusChartCircumference;
      const segment: StatusChartSegment = {
        item,
        dasharray: `${segmentLength} ${this.statusChartCircumference - segmentLength}`,
        dashoffset: -offset
      };
      offset += segmentLength;
      return segment;
    });
  }

  getStatusLabelStyle(index: number, items: AnalyticsStatusBreakdownDto[]): Record<string, string> {
    const angle = (this.getStatusLabelAngle(index, items) - 90) * (Math.PI / 180);
    const radius = 132;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;

    return {
      left: `calc(50% + ${x}px)`,
      top: `calc(50% + ${y}px)`,
      transform: `translate(${x >= 0 ? '0' : '-100%'}, -50%)`
    };
  }

  formatPercentage(value: number): string {
    return `${Math.round(Number(value) || 0)}%`;
  }

  getActiveStatusItem(items: AnalyticsStatusBreakdownDto[]): AnalyticsStatusBreakdownDto | null {
    const activeKey = this.activeStatusKey;
    if (!activeKey) {
      return null;
    }

    return items.find((item) => item.key === activeKey) ?? null;
  }

  setActiveStatus(key: string): void {
    this.activeStatusKey = key;
  }

  clearActiveStatus(): void {
    this.activeStatusKey = null;
  }

  formatAverageFirstResponse(value: number): string {
    const safeValue = Number(value) || 0;
    if (safeValue < 1) {
      return '<1 day';
    }

    const rounded = Math.round(safeValue);
    return rounded === 1 ? '~1 day' : `~${rounded} days`;
  }

  trackByStatus(_: number, item: AnalyticsStatusBreakdownDto): string {
    return item.key;
  }

  trackByStatusSegment(_: number, segment: StatusChartSegment): string {
    return segment.item.key;
  }

  private getStatusLabelAngle(index: number, items: AnalyticsStatusBreakdownDto[]): number {
    if (!items.length) {
      return 0;
    }

    const totalWeight = this.getStatusBreakdownTotal(items);
    if (totalWeight <= 0) {
      return (360 / items.length) * index;
    }

    let currentAngle = 0;
    for (let currentIndex = 0; currentIndex < items.length; currentIndex++) {
      const segmentSize = this.getStatusSegmentDegrees(items[currentIndex], items);
      if (currentIndex === index) {
        return currentAngle + segmentSize / 2;
      }

      currentAngle += segmentSize;
    }

    return 0;
  }

  private getStatusSegmentDegrees(item: AnalyticsStatusBreakdownDto, items: AnalyticsStatusBreakdownDto[]): number {
    const totalWeight = this.getStatusBreakdownTotal(items);
    if (totalWeight > 0) {
      return (Math.max(0, Number(item.totalIdeas) || 0) / totalWeight) * 360;
    }

    return Math.max(0, Number(item.percentage) || 0) * 3.6;
  }

  private getStatusBreakdownTotal(items: AnalyticsStatusBreakdownDto[]): number {
    return items.reduce((sum, item) => sum + Math.max(0, Number(item.totalIdeas) || 0), 0);
  }
}
